import { useModal } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';

import { ModalStackParamsType, modals } from '@modals';
import { ButtonStyleEnum } from '@components';
import { parseApiError } from '@utils';
import { useAuthModel } from '@models/AuthModel';
import { ToastService } from '@services';

interface IHandleErrorParams {
  error: unknown;
  email?: string;
  onSuccess?(): void;
}

type RestoreAccountParamsType = IHandleErrorParams &
  Pick<Required<IHandleErrorParams>, 'email'>;

export function useAuthErrorHandler() {
  const { openModal, closeModal } = useModal<ModalStackParamsType>();
  const { t } = useTranslation();
  const authModel = useAuthModel();

  async function restoreAccount({
    email,
    onSuccess,
  }: RestoreAccountParamsType) {
    try {
      await authModel.restoreAccount.run({ email });

      ToastService.showSuccess(
        t('common.restoreAccount.successMessage'),
      );

      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  function askRestoreAccount(params: IHandleErrorParams) {
    if (typeof params.email === 'undefined') {
      return;
    }

    openModal(modals.ConfirmationModal, {
      title: t('common.restoreAccount.title'),
      subtitle: t('common.restoreAccount.subtitle'),
      buttons: [
        {
          style: ButtonStyleEnum.Flat,
          text: t('common.restoreAccount.yes'),
        },
        { text: t('common.restoreAccount.cancel') },
      ],
      onConfirm: () => {
        restoreAccount(params as RestoreAccountParamsType);
        closeModal();
      },
    });
  }

  async function handleError(params: IHandleErrorParams) {
    const errorData = parseApiError(params.error);

    switch (errorData.errorCode) {
      case 91: /* 91 - user deleted */ {
        return askRestoreAccount(params);
      }
      default: {
        ToastService.showError(errorData.message);
      }
    }
  }

  return {
    handleError,
  };
}
